import React, { useState, useMemo } from 'react';
import {
  AlertTriangle,
  Search,
  Filter,
  CheckCircle2,
  Clock,
  MessageSquare,
  Shield,
  XCircle,
  FileText,
  User,
  ArrowRight,
} from 'lucide-react';
import type { AdminDisputeItem } from '../../types';

interface AdminDisputesSectionProps {
  disputes: AdminDisputeItem[];
  onResolve: (
    disputeId: string,
    status: 'investigating' | 'resolved' | 'dismissed',
    resolutionNotes: string
  ) => Promise<void>;
  isProcessing: boolean;
}

export const AdminDisputesSection: React.FC<AdminDisputesSectionProps> = ({
  disputes,
  onResolve,
  isProcessing,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedDispute, setSelectedDispute] = useState<AdminDisputeItem | null>(null);
  const [resolveModal, setResolveModal] = useState<{
    isOpen: boolean;
    dispute: AdminDisputeItem | null;
  }>({
    isOpen: false,
    dispute: null,
  });
  const [newStatus, setNewStatus] = useState<'investigating' | 'resolved' | 'dismissed'>('resolved');
  const [resolutionNotes, setResolutionNotes] = useState('');

  const filteredDisputes = useMemo(() => {
    return disputes.filter((d) => {
      const reason = (d.reason || '').toLowerCase();
      const desc = (d.description || '').toLowerCase();
      const raised = (d.raisedByName || '').toLowerCase();
      const job = (d.jobTitle || '').toLowerCase();
      const term = searchTerm.toLowerCase();

      const matchesSearch =
        !searchTerm || reason.includes(term) || desc.includes(term) || raised.includes(term) || job.includes(term);

      const matchesStatus =
        statusFilter === 'all' || d.status === statusFilter;

      return matchesSearch && matchesStatus;
    });
  }, [disputes, searchTerm, statusFilter]);

  const handleOpenResolve = (dispute: AdminDisputeItem) => {
    setResolveModal({ isOpen: true, dispute });
    setNewStatus(dispute.status === 'open' ? 'investigating' : 'resolved');
    setResolutionNotes(
      dispute.resolutionNotes ||
        'Dispute mediated between customer and artisan. Scope adjustment agreement verified.'
    );
  };

  const handleConfirmResolve = async () => {
    if (!resolveModal.dispute) return;
    await onResolve(resolveModal.dispute.id, newStatus, resolutionNotes);
    setResolveModal({ isOpen: false, dispute: null });
    if (selectedDispute?.id === resolveModal.dispute.id) {
      setSelectedDispute(null);
    }
  };

  return (
    <div id="admin-disputes-section" className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-slate-900 border border-slate-800 p-5 rounded-2xl">
        <div>
          <h2 className="text-base font-bold text-white flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-rose-400" />
            <span>Dispute & Conflict Resolution Desk ({disputes.length})</span>
          </h2>
          <p className="text-xs text-slate-400 mt-0.5">
            Mediate service delivery discrepancies, audit customer-artisan communication, and record binding resolution notes.
          </p>
        </div>

        <div className="flex items-center gap-2 text-xs">
          <span className="px-2.5 py-1 rounded-lg bg-rose-500/10 text-rose-300 font-semibold border border-rose-500/20">
            {disputes.filter((d) => d.status === 'open').length} Open
          </span>
          <span className="px-2.5 py-1 rounded-lg bg-amber-500/10 text-amber-300 font-semibold border border-amber-500/20">
            {disputes.filter((d) => d.status === 'investigating').length} Investigating
          </span>
          <span className="px-2.5 py-1 rounded-lg bg-emerald-500/10 text-emerald-400 font-semibold border border-emerald-500/20">
            {disputes.filter((d) => d.status === 'resolved').length} Resolved
          </span>
        </div>
      </div>

      {/* Filters */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="relative">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-3" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search disputes by job, customer, or reason..."
            className="w-full bg-slate-900 border border-slate-800 rounded-xl pl-9 pr-4 py-2.5 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-rose-500"
          />
        </div>

        <div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="w-full bg-slate-900 border border-slate-800 rounded-xl px-3 py-2.5 text-xs text-slate-300 focus:outline-none"
          >
            <option value="all">All Case Statuses</option>
            <option value="open">Open Cases</option>
            <option value="investigating">Under Investigation</option>
            <option value="resolved">Resolved</option>
            <option value="dismissed">Dismissed</option>
          </select>
        </div>
      </div>

      {/* Disputes Cards List */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {filteredDisputes.length === 0 ? (
          <div className="md:col-span-2 py-12 text-center bg-slate-900 border border-slate-800 rounded-2xl">
            <CheckCircle2 className="w-8 h-8 text-emerald-400 mx-auto mb-2 opacity-80" />
            <p className="text-xs font-semibold text-slate-300">Zero disputes in this view.</p>
          </div>
        ) : (
          filteredDisputes.map((disp) => {
            const isOpen = disp.status === 'open';

            return (
              <div
                key={disp.id}
                className={`p-5 bg-slate-900 border rounded-2xl space-y-3 flex flex-col justify-between transition-all ${
                  isOpen ? 'border-rose-500/40 bg-rose-950/10' : 'border-slate-800'
                }`}
              >
                <div>
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-white text-xs flex items-center gap-1.5">
                      <span
                        className={`w-2 h-2 rounded-full ${
                          isOpen ? 'bg-rose-500 animate-pulse' : 'bg-emerald-400'
                        }`}
                      ></span>
                      {disp.reason}
                    </span>
                    <span
                      className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${
                        isOpen
                          ? 'bg-rose-500/20 text-rose-300'
                          : disp.status === 'investigating'
                          ? 'bg-amber-500/20 text-amber-300'
                          : 'bg-emerald-500/20 text-emerald-300'
                      }`}
                    >
                      {disp.status}
                    </span>
                  </div>

                  <p className="text-xs text-slate-300 mt-2 leading-relaxed">
                    {disp.description}
                  </p>

                  <div className="mt-3 p-3 bg-slate-950/60 rounded-xl space-y-1 text-[11px] border border-slate-800/80">
                    <div className="flex justify-between">
                      <span className="text-slate-400">Job:</span>
                      <span className="text-white font-medium">{disp.jobTitle || disp.jobId}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-400">Raised By:</span>
                      <span className="text-slate-200">{disp.raisedByName || disp.raisedBy}</span>
                    </div>
                    {disp.resolutionNotes && (
                      <div className="pt-1 text-emerald-400 border-t border-slate-800/60">
                        <strong>Resolution:</strong> {disp.resolutionNotes}
                      </div>
                    )}
                  </div>
                </div>

                <div className="pt-2 border-t border-slate-800/80 flex items-center justify-between">
                  <span className="text-[11px] text-slate-500 font-mono">
                    {new Date(disp.createdAt).toLocaleDateString()}
                  </span>

                  <button
                    onClick={() => handleOpenResolve(disp)}
                    className="px-3 py-1.5 bg-slate-800 hover:bg-slate-750 text-emerald-400 hover:text-emerald-300 border border-slate-700 rounded-lg text-xs font-bold transition-colors"
                  >
                    Manage & Resolve Case
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Resolution Modal */}
      {resolveModal.isOpen && resolveModal.dispute && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm animate-fade-in">
          <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-md p-6 space-y-4 shadow-2xl">
            <h3 className="text-base font-bold text-white flex items-center gap-2">
              <Shield className="w-5 h-5 text-emerald-400" />
              <span>Mediate Dispute Case</span>
            </h3>

            <div>
              <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">
                Update Case Status:
              </label>
              <select
                value={newStatus}
                onChange={(e: any) => setNewStatus(e.target.value)}
                className="w-full bg-slate-950 border border-slate-800 rounded-xl p-2.5 text-xs text-white"
              >
                <option value="investigating">Under Investigation</option>
                <option value="resolved">Resolved (Agreement reached)</option>
                <option value="dismissed">Dismissed (No violation found)</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">
                Admin Resolution Notes & Binding Terms:
              </label>
              <textarea
                rows={3}
                value={resolutionNotes}
                onChange={(e) => setResolutionNotes(e.target.value)}
                placeholder="Enter mediation terms..."
                className="w-full bg-slate-950 border border-slate-800 rounded-xl p-3 text-xs text-white placeholder-slate-500 focus:outline-none"
              />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <button
                onClick={() => setResolveModal({ isOpen: false, dispute: null })}
                disabled={isProcessing}
                className="px-4 py-2 bg-slate-800 text-slate-300 text-xs font-semibold rounded-xl"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmResolve}
                disabled={isProcessing || !resolutionNotes.trim()}
                className="px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-slate-950 text-xs font-bold rounded-xl"
              >
                Save Resolution
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
